/**
 * conversationsSlice — sidebar + active conversation lifecycle.
 *
 * State:
 *   - `conversations`: keyed by id for O(1) read/patch
 *   - `orderedIds`: explicit order so list rendering doesn't depend on
 *     Object.keys() iteration order (which is insertion-order but breaks
 *     when entries are mutated)
 *   - `currentId`: which conversation the chat pane shows
 *   - `loading`: TanStack Query hydrates this on initial fetch
 *
 * Actions are pure store mutations — async (REST/WS) lives in hooks.
 */

import type { StateCreator } from "zustand";
import type { ChatConversation, ConversationConfig } from "./types";
import type { ChatStore } from "./chatStore";

export interface ConversationsSlice {
  conversations: Record<string, ChatConversation>;
  orderedIds: string[];
  currentId: string | null;
  loading: boolean;
  /**
   * Conversations created locally in THIS session (via the composer's first
   * send or the "Nuova chat" button). Their message history is authored
   * entirely by the live WS/REST turn that follows — there is nothing to
   * load from the server, and fetching `getConversation` for them races the
   * optimistic user turn (the detail fetch can resolve BEFORE the BE has
   * persisted the just-sent message, then `hydrateMessagesFor` REPLACES the
   * list with an empty/partial rebuild → the first user bubble vanishes).
   * `useConversationDetail` skips the fetch for these ids. A page reload
   * clears the set, so on the next session the now-persisted conversations
   * hydrate from the server as usual.
   */
  locallyCreatedIds: Record<string, true>;

  selectConversation: (id: string | null) => void;
  upsertConversation: (conv: ChatConversation) => void;
  markLocallyCreated: (id: string) => void;
  removeConversation: (id: string) => void;
  setTitle: (id: string, title: string) => void;
  setConfig: (id: string, partial: Partial<ConversationConfig>) => void;
  setError: (id: string, error: string | undefined) => void;
  setReplayInProgress: (id: string, value: boolean) => void;
  setConversationsLoading: (b: boolean) => void;
  hydrateConversations: (list: ChatConversation[]) => void;
}

export const createConversationsSlice: StateCreator<
  ChatStore,
  [],
  [],
  ConversationsSlice
> = (set) => ({
  conversations: {},
  orderedIds: [],
  currentId: null,
  loading: false,
  locallyCreatedIds: {},

  selectConversation: (id) => set({ currentId: id }),

  markLocallyCreated: (id) =>
    set((state) =>
      state.locallyCreatedIds[id]
        ? state
        : { locallyCreatedIds: { ...state.locallyCreatedIds, [id]: true } },
    ),

  upsertConversation: (conv) =>
    set((state) => {
      const exists = state.conversations[conv.id] !== undefined;
      return {
        conversations: { ...state.conversations, [conv.id]: conv },
        orderedIds: exists ? state.orderedIds : [conv.id, ...state.orderedIds],
      };
    }),

  removeConversation: (id) =>
    set((state) => {
      if (state.conversations[id] === undefined) return state;
      const { [id]: _, ...rest } = state.conversations;
      const { [id]: _local, ...restLocal } = state.locallyCreatedIds;
      return {
        conversations: rest,
        orderedIds: state.orderedIds.filter((x) => x !== id),
        currentId: state.currentId === id ? null : state.currentId,
        locallyCreatedIds: restLocal,
      };
    }),

  setTitle: (id, title) =>
    set((state) => {
      const existing = state.conversations[id];
      if (!existing) return state;
      return {
        conversations: {
          ...state.conversations,
          [id]: { ...existing, title },
        },
      };
    }),

  setConfig: (id, partial) =>
    set((state) => {
      const existing = state.conversations[id];
      if (!existing) return state;
      return {
        conversations: {
          ...state.conversations,
          [id]: {
            ...existing,
            config: { ...existing.config, ...partial },
          },
        },
      };
    }),

  setError: (id, error) =>
    set((state) => {
      const existing = state.conversations[id];
      if (!existing) return state;
      return {
        conversations: {
          ...state.conversations,
          [id]: { ...existing, error },
        },
      };
    }),

  setReplayInProgress: (id, value) =>
    set((state) => {
      const existing = state.conversations[id];
      if (!existing) return state;
      return {
        conversations: {
          ...state.conversations,
          [id]: { ...existing, replayInProgress: value },
        },
      };
    }),

  setConversationsLoading: (b) => set({ loading: b }),

  hydrateConversations: (list) =>
    set((state) => {
      const byId: Record<string, ChatConversation> = { ...state.conversations };
      const ordered: string[] = [];
      for (const c of list) {
        // Merge over the in-store entry so client-only optimistic fields
        // (notably ``project_id`` on a freshly-created chat) survive a
        // BE round-trip while the migration adding the column is still
        // rolling out. ``last_update_time`` / ``title`` are always
        // BE-canonical, so the spread order below lets ``c`` win on
        // those; the prior in-store ``project_id`` only fills the gap
        // when ``c.project_id`` is nullish (legacy row from BE without
        // the column yet).
        const prior = state.conversations[c.id];
        // Exact placeholder produced by the existing owner-gated persist merge.
        const persistedPlaceholder = prior?.user_id === "" && prior.title === ""
          && prior.updated_at === "" && prior.queries.length === 0
          && prior.replayInProgress === false;
        byId[c.id] = {
          ...prior,
          ...c,
          // REST summaries carry composer defaults, not saved client choices.
          // Keep the same actor's config or an owner-gated persisted placeholder
          // when its canonical nonempty actor arrives. Other actors stay fresh.
          config: prior && c.user_id !== "" && (prior.user_id === c.user_id || persistedPlaceholder)
            ? prior.config
            : c.config,
          project_id: c.project_id ?? prior?.project_id ?? null,
        };
        ordered.push(c.id);
      }
      return { conversations: byId, orderedIds: ordered };
    }),
});
